import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useSite, UI, resolveImg } from "@/lib/site";
import { MaskLine, Reveal, EASE_LUX } from "@/components/Reveal";
import { WaButton, InstaButton } from "@/components/WaButton";
import { Marquee } from "@/components/Marquee";
import { Stages, Steps } from "@/components/Stages";
import { Stars } from "@/components/Stars";
import { Contact } from "@/components/Contact";
import { Icon } from "@/components/Icon";
import { CasesGrid } from "@/components/home/Cases";
import { CaseModal } from "@/components/CaseModal";
import { useLenis, scrollToId } from "@/components/SmoothScroll";

const DirHero = ({ d, idx, total }) => {
  const { t } = useSite();
  const lenis = useLenis();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const name = t(d.title);
  return (
    <section ref={ref} data-testid="direction-hero" className="grain relative overflow-hidden bg-[#f3eadb] px-5 pb-20 pt-32 md:px-10 md:pb-28 md:pt-40">
      <img src="/media/silk-light.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#fbf7ef] via-[#fbf7ef]/85 to-[#fbf7ef]/30" />
      <div className="relative mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8a6424]">
            <Link to="/" className="hover:text-[#1d1712]" data-testid="breadcrumb-home">{t(UI.home)}</Link>
            <span>/</span><span className="text-[#1d1712]">{name}</span>
          </nav>
          <p className="font-display mt-8 text-2xl italic text-[#8a6424]">{String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</p>
          <h1 className="font-display mt-2 text-[3rem] font-medium leading-[0.95] tracking-[-0.02em] text-[#1d1712] sm:text-7xl lg:text-[5.6rem]" data-testid="direction-title">
            <MaskLine delay={0.15}>{t(d.pageTitle)}</MaskLine>
          </h1>
          <motion.p className="mt-8 max-w-2xl text-base leading-relaxed text-[#4a3f34] md:text-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }}>
            {t(d.intro)}
          </motion.p>
          <motion.div className="mt-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }}>
            <p className="eyebrow">{t(UI.categories)}</p>
            <ul className="mt-4 flex flex-wrap gap-2">
              {(Array.isArray(d.categories) ? d.categories : []).map((c, i) => (
                <li key={i} className="rounded-full border border-[#b8893b]/40 bg-[#fbf7ef]/70 px-4 py-2 text-sm text-[#2a2119] backdrop-blur" data-testid={`direction-category-${i}`}>{t(c)}</li>
              ))}
            </ul>
          </motion.div>
          <motion.div className="mt-10 flex flex-wrap gap-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.85 }}>
            <WaButton label={t(UI.learnMore)} text={`${t(UI.waMsg)} — ${name}`} testId="direction-whatsapp-btn" />
            <InstaButton testId="direction-instagram-btn" />
            <button className="btn-ghost" onClick={() => scrollToId(lenis, "dir-cases")} data-testid="direction-cases-btn">{t(UI.casesEyebrow)} <ArrowDown className="h-4 w-4" /></button>
          </motion.div>
        </div>
        <div className="relative lg:col-span-5">
          <motion.div className="arch relative mx-auto aspect-[4/5] w-full max-w-[440px] overflow-hidden bg-[#1d1712]"
            initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.4, ease: EASE_LUX, delay: 0.2 }}>
            <motion.img style={{ y, scale: 1.2 }} src={resolveImg(d.image)} alt={name} className="absolute inset-0 h-full w-full object-cover" data-testid="direction-main-photo" />
          </motion.div>
          {d.image2 && (
            <motion.div style={{ y: y2 }} className="absolute -bottom-8 left-0 w-40 overflow-hidden rounded-2xl border-4 border-[#fbf7ef] shadow-2xl sm:w-52 lg:-left-10">
              <img src={resolveImg(d.image2)} alt="" className="aspect-[4/3] w-full object-cover" />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

const Included = ({ d }) => {
  const { t } = useSite();
  const items = Array.isArray(d.included) ? d.included : [];
  return (
    <section data-testid="included-section" className="bg-[#fbf7ef] px-5 py-24 md:px-10 md:py-36">
      <div className="mx-auto max-w-[1400px]">
        <p className="eyebrow">{t(d.title)}</p>
        <h2 className="font-display mt-6 text-4xl font-medium leading-[1.02] text-[#1d1712] sm:text-5xl lg:text-6xl"><MaskLine inView>{t(UI.included)}</MaskLine></h2>
        <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it, i) => {
            const n = items.length;
            const r = n % 3;
            const lgWide = (i === 0 && r > 0) || (i === n - 1 && r === 1);
            const smWide = i === 0 && n % 2 === 1;
            const span = `${smWide ? "sm:col-span-2" : ""} ${lgWide ? "lg:col-span-2" : "lg:col-span-1"}`;
            return (
            <Reveal key={i} delay={(i % 3) * 0.08} className={`group relative overflow-hidden rounded-[22px] border border-[#b8893b]/25 bg-[#f3eadb]/60 p-8 transition-[background-color,transform] duration-500 hover:-translate-y-1 hover:bg-[#1d1712] ${span}`} data-testid={`included-card-${i}`}>
              <div className="grid h-14 w-14 place-items-center rounded-full border border-[#b8893b]/50 text-[#8a6424] transition-colors duration-500 group-hover:border-[#f0d78e] group-hover:text-[#f0d78e]">
                <Icon name={it.icon} className="h-6 w-6" />
              </div>
              <h3 className="font-display mt-8 text-3xl font-medium text-[#1d1712] transition-colors duration-500 group-hover:text-[#fbf3e2]">{t(it.title)}</h3>
              <p className="mt-3 text-[0.95rem] leading-relaxed text-[#5b4f43] transition-colors duration-500 group-hover:text-[#d8ccb6]">{t(it.text)}</p>
              <span className="font-display absolute right-6 top-5 text-lg text-[#b8893b]/60">{String(i + 1).padStart(2, "0")}</span>
            </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const OtherDirs = ({ current }) => {
  const { t, content } = useSite();
  const others = (content?.directions || []).filter((x) => x.slug !== current);
  return (
    <section className="bg-[#fbf7ef] px-5 py-20 md:px-10" data-testid="other-directions">
      <div className="mx-auto max-w-[1400px]">
        <p className="eyebrow">{t(UI.otherDirs)}</p>
        <div className="mt-6">
          {others.map((o) => (
            <Link key={o.slug} to={`/direction/${o.slug}`} className="group flex items-center justify-between border-b border-[#b8893b]/25 py-6 md:py-8" data-testid={`other-direction-${o.slug}`}>
              <span className="font-display text-4xl text-[#1d1712] transition-[color,transform] duration-500 group-hover:translate-x-3 group-hover:text-[#8a6424] md:text-7xl">{t(o.title)}</span>
              <span className="hidden max-w-xs text-sm text-[#5b4f43] md:block">{t(o.short)}</span>
              <ArrowUpRight className="h-8 w-8 text-[#8a6424] transition-transform duration-500 group-hover:rotate-45" strokeWidth={1.2} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Direction() {
  const { slug } = useParams();
  const { t, content } = useSite();
  const [open, setOpen] = useState(null);
  const dirs = Array.isArray(content?.directions) ? content.directions : [];
  const idx = dirs.findIndex((x) => x.slug === slug);
  const d = dirs[idx];
  useEffect(() => { if (d) document.title = `${t(d.pageTitle)} — Dari Event`; }, [d, t]);
  if (!d) return <Navigate to="/" replace />;
  const ids = Array.isArray(d.caseIds) ? d.caseIds : [];
  const cases = (content?.cases || []).filter((c) => ids.includes(c.id)).sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));

  return (
    <main data-testid={`direction-page-${slug}`} key={slug}>
      <DirHero d={d} idx={idx} total={dirs.length} />
      <Marquee items={(d.categories || []).map(t)} dark testId="direction-marquee" />
      <Included d={d} />
      {d.stars && Array.isArray(d.stars.items) && d.stars.items.length > 0 && <Stars s={d.stars} />}
      {Array.isArray(d.steps) && d.steps.length > 0 ? <Steps steps={d.steps} /> : <Stages />}
      <section id="dir-cases" data-testid="direction-cases-section" className="bg-[#0e0c0a] px-5 py-24 text-[#f6ecd9] md:px-10 md:py-36">
        <div className="mx-auto max-w-[1400px]">
          <p className="eyebrow !text-[#e6ca7a]">{t(d.title)}</p>
          <h2 className="font-display mt-6 text-4xl font-medium leading-[1.02] sm:text-5xl lg:text-6xl"><MaskLine inView>{t(UI.dirCases)}</MaskLine></h2>
          <div className="mt-14"><CasesGrid items={cases} onOpen={setOpen} /></div>
        </div>
        <CaseModal item={open} onClose={() => setOpen(null)} />
      </section>
      <OtherDirs current={slug} />
      <Contact defaultDirection={slug} />
    </main>
  );
}
